import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import { useUpdatePlace } from '@/api';
import { parseDisplayName } from '@/lib/utils';
import type { Lead } from '@/types/leads';
import styles from './PlaceEditDrawer.module.css';

interface ApiFieldErrors {
  displayName?: string[];
  formattedAddress?: string[];
  internationalPhoneNumber?: string[];
  websiteUri?: string[];
  [key: string]: string[] | string | undefined;
}

interface PlaceEditDrawerProps {
  isOpen: boolean;
  place: Lead | null;
  onClose: () => void;
  onSuccess?: (updated: Lead) => void;
}

export function PlaceEditDrawer({ isOpen, place, onClose, onSuccess }: PlaceEditDrawerProps) {
  const [displayName, setDisplayName] = useState('');
  const [formattedAddress, setFormattedAddress] = useState('');
  const [internationalPhoneNumber, setInternationalPhoneNumber] = useState('');
  const [websiteUri, setWebsiteUri] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ApiFieldErrors>({});
  const firstInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: updatePlace, isPending } = useUpdatePlace();

  // Hydrate form from the place record when the drawer opens
  useEffect(() => {
    if (isOpen && place) {
      setDisplayName(parseDisplayName(place.displayName)?.text || '');
      setFormattedAddress(place.formattedAddress || '');
      setInternationalPhoneNumber(place.internationalPhoneNumber || '');
      setWebsiteUri((place as Lead & { websiteUri?: string | null }).websiteUri || '');
      setFieldErrors({});
      const t = setTimeout(() => firstInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen, place]);

  const handleClose = useCallback(() => {
    if (isPending) return;
    onClose();
  }, [isPending, onClose]);

  // Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!place) return;
    setFieldErrors({});

    try {
      const updated = await updatePlace({
        id: place.id,
        payload: {
          displayName: displayName.trim(),
          formattedAddress: formattedAddress.trim(),
          internationalPhoneNumber: internationalPhoneNumber.trim(),
          websiteUri: websiteUri.trim(),
        },
      });
      toast.success(`Updated ${displayName || 'business'}`, { duration: 3000 });
      onSuccess?.(updated);
      onClose();
    } catch (err) {
      const axiosErr = err as AxiosError<ApiFieldErrors>;
      const status = axiosErr.response?.status;
      const data = axiosErr.response?.data;

      if (status === 400 && data) {
        setFieldErrors(data);
      } else if (status === 404) {
        toast.error('Business not found. It may have been deleted.', { duration: 8000 });
      } else if (status === 403) {
        toast.error("You don't have permission to edit this business.", { duration: 8000 });
      } else {
        toast.error('Could not save changes. Please try again.', { duration: 8000 });
      }
    }
  };

  const getFieldError = (field: keyof ApiFieldErrors): string | undefined => {
    const errs = fieldErrors[field];
    if (!errs) return undefined;
    if (Array.isArray(errs)) return errs[0];
    return String(errs);
  };

  if (!isOpen || !place) return null;

  return (
    <>
      <div className={styles.backdrop} onClick={handleClose} aria-hidden="true" />

      <div role="dialog" aria-modal="true" aria-label="Edit business" className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerTitle}>Edit business</h2>
            <p className={styles.drawerSubtitle}>
              Changes apply to every contact attached to this business.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className={styles.closeBtn}
            aria-label="Close drawer"
            disabled={isPending}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form} noValidate>
          <div className={styles.field}>
            <label htmlFor="place-name" className={styles.label}>
              Business name
            </label>
            <input
              ref={firstInputRef}
              id="place-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={`${styles.input} ${getFieldError('displayName') ? styles.inputError : ''}`}
              placeholder="Acme Coffee"
              disabled={isPending}
              autoComplete="organization"
            />
            {getFieldError('displayName') && (
              <span className={styles.fieldError}>{getFieldError('displayName')}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="place-address" className={styles.label}>
              Address
            </label>
            <input
              id="place-address"
              type="text"
              value={formattedAddress}
              onChange={(e) => setFormattedAddress(e.target.value)}
              className={`${styles.input} ${getFieldError('formattedAddress') ? styles.inputError : ''}`}
              placeholder="123 Main St, City"
              disabled={isPending}
              autoComplete="street-address"
            />
            {getFieldError('formattedAddress') && (
              <span className={styles.fieldError}>{getFieldError('formattedAddress')}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="place-phone" className={styles.label}>
              Phone
            </label>
            <input
              id="place-phone"
              type="tel"
              value={internationalPhoneNumber}
              onChange={(e) => setInternationalPhoneNumber(e.target.value)}
              className={`${styles.input} ${getFieldError('internationalPhoneNumber') ? styles.inputError : ''}`}
              placeholder="+51 999 123 456"
              disabled={isPending}
              autoComplete="tel"
            />
            {getFieldError('internationalPhoneNumber') && (
              <span className={styles.fieldError}>{getFieldError('internationalPhoneNumber')}</span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="place-website" className={styles.label}>
              Website
            </label>
            <input
              id="place-website"
              type="url"
              value={websiteUri}
              onChange={(e) => setWebsiteUri(e.target.value)}
              className={`${styles.input} ${getFieldError('websiteUri') ? styles.inputError : ''}`}
              placeholder="https://example.com"
              disabled={isPending}
              autoComplete="url"
            />
            {getFieldError('websiteUri') && (
              <span className={styles.fieldError}>{getFieldError('websiteUri')}</span>
            )}
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              className={styles.cancelBtn}
              disabled={isPending}
            >
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className={styles.spinner} />
                  Saving...
                </>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
